import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmt } from "@/lib/status";

const OPTS = [
  ["none", "No provider step - complete as soon as the patient signs (default)", "In-person consents finalize immediately after the patient signs and accepts, as before."],
  ["checkbox", "Checkbox confirmation", "Provider ticks the verification statement on the iPad right after the patient signs, while signed in to an active session."],
  ["pin", "Checkbox + secure provider PIN", "Personal 6-digit PIN, salted bcrypt hash, 5 tries then 15-minute lockout, reset after 10 failures. Set up after re-authentication."],
  ["reauth", "Checkbox + password/SSO reauthentication", "Provider re-enters their sign-in credentials (existing login system) if not done in the last 10 minutes."],
];

export default function VerificationSettings() {
  const [d, setD] = useState(null);
  const load = () => api.get("/practice/verification").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-sm text-slate-500">Loading...</p>;
  const setMethod = (m) => api.put("/practice/verification", { method: m }).then(() => { toast.success("Verification method updated"); load(); }).catch((e) => toast.error(errMsg(e)));
  const reset = (u) => window.confirm(`Reset the verification PIN for ${u.name}? They must re-authenticate and create a new PIN.`) &&
    api.post(`/practice/members/${u.user_id}/verification-pin/reset`).then(() => { toast.success("PIN reset"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-4" data-testid="verification-settings">
      <section className="rounded-xl border bg-white p-4">
        <h3 className="font-semibold">Provider verification before finalizing in-person consents</h3>
        <p className="text-xs text-slate-500 mt-1">Statement {d.statement.version}: "{d.statement.text}" This is an attestation, not a provider signature.</p>
        <RadioGroup value={d.method} onValueChange={setMethod} className="mt-4 space-y-3">
          {OPTS.map(([k, l, s]) => (
            <label key={k} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${d.method === k ? "border-[var(--brand)] bg-[var(--brand-soft)]" : ""}`}>
              <RadioGroupItem value={k} className="mt-1" data-testid={`verification-method-${k}`} />
              <span><b className="text-sm">{l}</b><span className="block text-xs text-slate-600">{s}</span></span>
            </label>))}
        </RadioGroup>
      </section>
      <section className="rounded-xl border bg-white p-4">
        <h3 className="font-semibold">Provider PINs</h3>
        <p className="text-xs text-slate-500">You can reset a PIN but never view or set it.</p>
        <div className="mt-3 divide-y">
          {(d.providers || []).map((u) => (
            <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`verification-provider-${u.user_id}`}>
              <span><b>{u.name}</b> <span className="text-slate-500">{u.email}</span></span>
              <span className="text-xs">{u.has_pin ? `PIN set ${fmt(u.set_at)}` : u.reset_required ? "Reset - awaiting new PIN" : "No PIN"}{u.locked && <b className="text-red-700"> - locked</b>}</span>
              {(u.has_pin || u.locked) && <Button size="sm" variant="outline" className="h-8" onClick={() => reset(u)} data-testid={`verification-reset-${u.user_id}`}>Reset PIN</Button>}
            </div>))}
          {!d.providers?.length && <p className="text-sm text-slate-500 py-2">No providers in this organization.</p>}
        </div>
      </section>
    </div>
  );
}
